import { InputRichText } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import { ampli } from 'lib-shared-tracking';
import { Conversation } from 'lib-shared-types-api/src/conversations';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useMount } from 'react-use';

import { QuickRepliesContent } from './components/quickRepliesContent/quickRepliesContent';
import { usePreloadRecommendedQuickReplies } from './components/quickRepliesContent/util/usePreloadRecommendedQuickReplies';
import { QuickRepliesTrigger } from './components/quickRepliesTrigger/quickRepliesTrigger';
import styles from './editorQuickRepliesDropdown.module.scss';
import { ChangeSubjectFn } from './editorQuickRepliesDropdown.types';

type PopoverContent = {
  side?: Popover.PopoverContentProps['side'];
  align?: Popover.PopoverContentProps['align'];
};

type EditorQuickRepliesDropdownProps = {
  changeSubject?: ChangeSubjectFn;
  enduserId?: string | null;
  customWindow?: Window;
  conversationId?: string;
  locale?: string;
  outboundMessages?: number;
  channel?: string;
  popoverContent?: PopoverContent;
  conversation?: Conversation;
};

export const EditorQuickRepliesDropdown = ({
  changeSubject,
  enduserId = null,
  customWindow,
  conversationId,
  locale,
  outboundMessages = 0,
  channel,
  popoverContent,
  conversation,
}: EditorQuickRepliesDropdownProps): JSX.Element | null => {
  usePreloadRecommendedQuickReplies({
    conversation,
    channel,
    outboundMessages,
    locale,
    conversationId,
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const editorContext = useContext(InputRichText.Context);

  const handleOpen = () => {
    setIsPopoverOpen(true);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
  };

  const createTrigger = useCallback(() => {
    return {
      id: 'quick-replies',
      trigger: /(^|\s)=/,
      minChars: 0,
      allowSpaces: true,
      customWindow,
      component: (
        <QuickRepliesTrigger
          changeSubject={changeSubject}
          onOpen={handleOpen}
          onClose={handleClose}
          enduserId={enduserId}
        />
      ),
      popoverContent,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeSubject, customWindow, enduserId]);

  useMount(() => {
    editorContext.triggers.add(createTrigger());
  });

  useEffect(() => {
    editorContext.triggers.update(createTrigger());
  }, [createTrigger, editorContext.triggers]);

  return (
    <div>
      <Popover.Root
        onOpenChange={(isOpen) => {
          if (isOpen) {
            ampli.quickRepliesOpened({ openedBy: 'button' });
            setIsPopoverOpen(true);
          } else {
            setIsPopoverOpen(false);
          }
        }}
      >
        <Popover.Trigger asChild>
          <div
            className={styles.buttonWrapper}
            role="menu"
            aria-expanded={isPopoverOpen}
          >
            <InputRichText.ToolbarButton icon="bolt" />
          </div>
        </Popover.Trigger>
        <Popover.Content className={styles.root} align="start" side="top">
          <QuickRepliesContent
            changeSubject={changeSubject}
            enduserId={enduserId}
          />
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};
